import type { z } from "zod";
import type { AnyEventDefinition, EventDefinition } from "./_define";

import { contentVisible } from "./content-visible";
import { ctaClicked } from "./cta";
import { entrypointViewed } from "./entrypoint";
import { screenViewed } from "./screen-viewed";
import {
  paywallViewed,
  billingCadenceSwitched,
  productCardSelected,
} from "./paywall";
import {
  checkoutViewed,
  checkoutMethodLoaded,
  checkoutAbandoned,
  paymentSubmitted,
  paymentSucceeded,
} from "./checkout";
import {
  subscriptionCreated,
  subscriptionStatusUpdated,
  subscriptionCanceled,
  subscriptionRevenueRecognized,
  subscriptionRefunded,
  subscriptionChargeDisputed,
  cancelIntentSet,
  cancelIntentReversed,
  subscriptionPaymentFailed,
  subscriptionPaymentRecovered,
  trialEnded,
  subscriptionCanceledFeedback,
} from "./subscription";
import { subscriptionStatusCensus } from "./subscription-census";
import { headlessIntakeAttempted } from "./headless-intake";
import { nightwatchArmed, nightwatchIntakeHardStop } from "./nightwatch";
import {
  visitRequestStarted,
  visitRequestCancelled,
  visitCompleted,
  visitCancelled,
  visitActive,
  visitProviderAssigned,
  caseCreated,
  conditionSelected,
  childSelector,
  chatMessageSent,
  chatFirstResponseReceived,
} from "./visit";
import {
  mdiQuestionnaireLoaded,
  mdiIntakeStarted,
  mdiIntakeCompleted,
  mdiQuestionnaireUnload,
  mdiQuestionnaireStep,
  mdiEncounterCreated,
  mdiPatientCreated,
  mdiQuestionSkipped,
  mdiDeadEndQuestion,
  mdiPatientLoggedIn,
} from "./mdi";
import {
  profileSubmitted,
  profileCancelled,
  careProfileCompleted,
  accountCreated,
  accountExistingReturn,
  hipaaConsentAccepted,
} from "./profile";
import { errorShown } from "./error";
import {
  onboardingWelcomeContinue,
  triageRequestConfirmed,
  triageRequestCancelled,
  manageSubscription,
  termsConditions,
  contactSupport,
  subCancelReactivate,
  paymentErrManage,
  shortlinkFollowed,
  npsCompleted,
  uiDismissed,
} from "./misc";
import {
  buttonPressed,
  learnMoreClicked,
  startTrialClicked,
  dismiss,
  homeDismissed,
  infoCardDismissed,
  subscriptionFlowAbandoned,
  homeEmptyStateViewed,
  confirmVisitRequestError,
  billingCycleSwitched,
  formSubmitted,
  formAbandoned,
  expressCheckoutConfirmed,
  expressCheckoutPressed,
  conversionDrawerOpened,
  rescuedToRx,
  menuOpened,
  paywallDismissed,
  paywallStartFreeTrial,
  paywallTwoProductContinue,
  subscriptionGateOpened,
  subscriptionGateDismissed,
  subscriptionGateProductSelected,
  subscriptionGatePaymentStarted,
  dismissableCtaButtonPressed,
  triagedCtaButtonPressed,
} from "./legacy";

/**
 * Single source of truth for all Statsig events. Keyed by event name.
 *
 * Adding a new event: create a file under `packages/statsig/src/events/`,
 * import its definition, and register it here. The snapshot test will fail
 * until you regenerate it (`npm test -- -u`), forcing a deliberate review.
 *
 * Changing an existing event: see the policy in `packages/statsig/TAXONOMY.md`.
 * Breaking changes require an `event_version` bump and a dual-fire window.
 */
export const EVENT_REGISTRY = {
  cta_clicked: ctaClicked,
  entrypoint_viewed: entrypointViewed,
  content_visible: contentVisible,
  screen_viewed: screenViewed,

  paywall_viewed: paywallViewed,
  billing_cadence_switched: billingCadenceSwitched,
  product_card_selected: productCardSelected,

  checkout_viewed: checkoutViewed,
  checkout_method_loaded: checkoutMethodLoaded,
  checkout_abandoned: checkoutAbandoned,
  payment_submitted: paymentSubmitted,
  payment_succeeded: paymentSucceeded,

  subscription_created: subscriptionCreated,
  subscription_status_updated: subscriptionStatusUpdated,
  subscription_canceled: subscriptionCanceled,
  subscription_revenue_recognized: subscriptionRevenueRecognized,
  subscription_refunded: subscriptionRefunded,
  subscription_charge_disputed: subscriptionChargeDisputed,
  cancel_intent_set: cancelIntentSet,
  cancel_intent_reversed: cancelIntentReversed,
  subscription_payment_failed: subscriptionPaymentFailed,
  subscription_payment_recovered: subscriptionPaymentRecovered,
  trial_ended: trialEnded,
  subscription_canceled_feedback: subscriptionCanceledFeedback,
  subscription_status_census: subscriptionStatusCensus,

  headless_intake_attempted: headlessIntakeAttempted,

  nightwatch_armed: nightwatchArmed,
  nightwatch_intake_hard_stop: nightwatchIntakeHardStop,

  visit_request_started: visitRequestStarted,
  visit_request_cancelled: visitRequestCancelled,
  visit_completed: visitCompleted,
  visit_cancelled: visitCancelled,
  visit_active: visitActive,
  visit_provider_assigned: visitProviderAssigned,
  case_created: caseCreated,
  condition_selected: conditionSelected,
  child_selector: childSelector,
  chat_message_sent: chatMessageSent,
  chat_first_response_received: chatFirstResponseReceived,

  mdi_questionnaire_loaded: mdiQuestionnaireLoaded,
  mdi_intake_started: mdiIntakeStarted,
  mdi_intake_completed: mdiIntakeCompleted,
  mdi_questionnaire_unload: mdiQuestionnaireUnload,
  mdi_questionnaire_step: mdiQuestionnaireStep,
  mdi_encounter_created: mdiEncounterCreated,
  mdi_patient_created: mdiPatientCreated,
  mdi_question_skipped: mdiQuestionSkipped,
  mdi_dead_end_question: mdiDeadEndQuestion,
  mdi_patient_logged_in: mdiPatientLoggedIn,

  profile_submitted: profileSubmitted,
  profile_cancelled: profileCancelled,
  care_profile_completed: careProfileCompleted,
  account_created: accountCreated,
  account_existing_return: accountExistingReturn,
  hipaa_consent_accepted: hipaaConsentAccepted,

  error_shown: errorShown,

  onboarding_welcome_continue: onboardingWelcomeContinue,
  triage_request_confirmed: triageRequestConfirmed,
  triage_request_cancelled: triageRequestCancelled,
  manage_subscription: manageSubscription,
  terms_conditions: termsConditions,
  contact_support: contactSupport,
  sub_cancel_reactivate: subCancelReactivate,
  payment_err_manage: paymentErrManage,
  shortlink_followed: shortlinkFollowed,
  nps_completed: npsCompleted,
  ui_dismissed: uiDismissed,

  // Legacy / unmodeled — see ./legacy.ts. Registered with permissive
  // schemas so the compile-time `RegisteredEventName` gate covers
  // pre-existing fire sites; cleanup tracked separately.
  button_pressed: buttonPressed,
  learn_more_clicked: learnMoreClicked,
  start_trial_clicked: startTrialClicked,
  dismiss: dismiss,
  home_dismissed: homeDismissed,
  info_card_dismissed: infoCardDismissed,
  subscription_flow_abandoned: subscriptionFlowAbandoned,
  home_empty_state_viewed: homeEmptyStateViewed,
  confirm_visit_request_error: confirmVisitRequestError,
  billing_cycle_switched: billingCycleSwitched,
  form_submitted: formSubmitted,
  form_abandoned: formAbandoned,
  express_checkout_confirmed: expressCheckoutConfirmed,
  express_checkout_pressed: expressCheckoutPressed,
  conversion_drawer_opened: conversionDrawerOpened,
  rescued_to_rx: rescuedToRx,
  menu_opened: menuOpened,
  paywall_dismissed: paywallDismissed,
  paywall_start_free_trial: paywallStartFreeTrial,
  paywall_two_product_continue: paywallTwoProductContinue,
  subscription_gate_opened: subscriptionGateOpened,
  subscription_gate_dismissed: subscriptionGateDismissed,
  subscription_gate_product_selected: subscriptionGateProductSelected,
  subscription_gate_payment_started: subscriptionGatePaymentStarted,
  dismissable_cta_button_pressed: dismissableCtaButtonPressed,
  triaged_cta_button_pressed: triagedCtaButtonPressed,
} as const satisfies Record<string, AnyEventDefinition>;

export type EventRegistry = typeof EVENT_REGISTRY;
export type RegisteredEventName = keyof EventRegistry;

export type EventDefinitionFor<N extends RegisteredEventName> = EventRegistry[N];
export type EventValueFor<N extends RegisteredEventName> = z.infer<
  EventRegistry[N]["valueSchema"]
>;
export type EventMetadataFor<N extends RegisteredEventName> = z.infer<
  EventRegistry[N]["metadataSchema"]
>;

export function getEventDefinition<N extends RegisteredEventName>(
  name: N
): EventDefinition<N, number, z.ZodTypeAny, z.ZodTypeAny> {
  return EVENT_REGISTRY[name] as unknown as EventDefinition<
    N,
    number,
    z.ZodTypeAny,
    z.ZodTypeAny
  >;
}
